/**
 * Privacy Policy for Attractr + Audience Lab — June 2026 Legal
 * Framework supplied by the client (Akuntsu Inc.).
 *
 * EN is the canonical text. FR is being prepared by the client; until
 * it lands, the FR locale shows a short notice and renders the
 * authoritative EN copy so users still see the current policy.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { ExternalLink } from '../../components';
import classNames from 'classnames';

import css from './PrivacyPolicy.module.css';
import { useIntl } from '../../util/reactIntl';

const EFFECTIVE_DATE = 'June 22, 2026';

const EnglishContent = () => (
  <>
    <p className={css.lastUpdated}>Effective Date: {EFFECTIVE_DATE}</p>

    <h2>1. Introduction &amp; Scope</h2>
    <p>
      Akuntsu Inc. ("Akuntsu", "we", "us") is the sole operator of the Services and the Data
      Controller of your personal information.
    </p>
    <p>
      Butterfly Innov ("Butterfly"), an independent company based in Serbia, provides software
      development and technical processing services to Akuntsu and acts strictly as a Data Processor
      on behalf of Akuntsu.
    </p>
    <p>
      This Privacy Policy explains how Akuntsu collects, uses, and discloses personal information
      through the Attractr platform and Audience Lab.
    </p>

    <h2>2. Compliance With Applicable Laws</h2>
    <p>
      We are committed to complying with applicable data protection laws, including Quebec's Law 25,
      Canada's Personal Information Protection and Electronic Documents Act (PIPEDA), and the
      European Union's General Data Protection Regulation (GDPR), when processing the personal
      information of individuals located in the relevant jurisdictions.
    </p>

    <h2>3. Our Privacy Officer</h2>
    <p>
      We have appointed Yuko Nakamura as our Person in Charge of the Protection of Personal
      Information (Privacy Officer). The Privacy Officer is responsible for overseeing our
      compliance with Law 25, handling data subject requests, and responding to any confidentiality
      incidents.
    </p>
    <p>You can contact our Privacy Officer at:</p>
    <p>
      Yuko Nakamura · Akuntsu Inc. · 4060 Boulevard St-Laurent, Suite 703, Montreal, QC H2W 1Y9,
      Canada ·{' '}
      <ExternalLink href="mailto:privacy@attractr.live">privacy@attractr.live</ExternalLink>
    </p>

    <h2>4. Information We Collect</h2>
    <h3>a. Identity &amp; Role-Based Data</h3>
    <ul>
      <li>
        For all users: name, email address, verified date of birth (to confirm 18+ eligibility), and
        phone number.
      </li>
      <li>
        For Suppliers: tax IDs (GST/HST/QST), business registration details, and banking information
        for payouts.
      </li>
      <li>For audience members: interests and creators supported.</li>
    </ul>
    <h3>b. Audience Lab Data</h3>
    <ul>
      <li>Forum posts, community comments, and educational progress tracking.</li>
      <li>Interaction data from WordPress and WooCommerce plugins.</li>
    </ul>
    <h3>c. Automated Technical Data</h3>
    <ul>
      <li>
        IP addresses, device information, and usage metrics from your interaction with the Services.
      </li>
    </ul>
    <h3>d. AI-Generated Insights</h3>
    <ul>
      <li>
        Predictive "Cultural Intelligence" signals derived from aggregated user interactions by the
        Clara AI (ClaraLive.ai) engine.
      </li>
      <li>
        We design our systems to avoid identifying specific individuals and do not use these outputs
        for individual-level profiling or decisions with legal or similarly significant effects.
      </li>
    </ul>

    <h2>5. Age Restrictions</h2>
    <p>
      The Services are not intended for use by individuals under the age of 18. We do not knowingly
      collect personal information from children. If we learn that we have collected personal
      information from a child under 18, we will take steps to delete that information as soon as
      possible.
    </p>
    <p>
      We reserve the right to use third-party identity verification services to confirm that users
      are 18 years of age or older. Verification providers act as data processors and are
      contractually obligated to protect your information and delete it once verification is
      complete.
    </p>

    <h2>6. How We Use Your Information</h2>
    <ul>
      <li>
        To provide and maintain the Services, including the Attractr marketplace, Audience Lab forum
        and educational content, and Clara AI cultural intelligence features.
      </li>
      <li>To process payments and payouts in connection with the Services.</li>
      <li>
        To communicate with you about your account, respond to your inquiries, and provide customer
        support.
      </li>
      <li>
        To personalize your experience on the Services, such as by tailoring content and
        recommendations to your interests.
      </li>
      <li>
        To generate anonymized, aggregated insights through the Clara AI engine to improve the
        Services and support performing arts market research.
      </li>
      <li>To detect, prevent, and address fraud, security issues, and technical problems.</li>
      <li>To enforce our Terms of Service and other policies governing use of the Services.</li>
    </ul>

    <h2>7. Sharing Your Information</h2>
    <ul>
      <li>
        <strong>With Suppliers:</strong> Where necessary to deliver a Supplier's paid or interactive
        offering, we may share limited participant information with that Supplier. Suppliers are
        required to handle this information in compliance with Law 25 and the GDPR.
      </li>
      <li>
        <strong>With our service providers:</strong> We may engage third-party companies to
        facilitate the Services. Our approved sub-processors are: Brevo SAS (newsletter and email
        communications), Airtable Inc. (CRM and member database management), Automattic Inc. /
        WordPress.com (website hosting and blog publishing), Sharetribe (marketplace
        infrastructure), Google Cloud (data storage), Sendbird (in-platform chat), Whereby (video
        streaming), and Stripe (payment processing). Each sub-processor has access to your personal
        information only to the extent needed to perform their functions and is obligated to
        maintain its confidentiality and security.
      </li>
      <li>
        <strong>With your consent:</strong> We may share your information with third parties when
        you have given us consent to do so.
      </li>
      <li>
        <strong>For legal reasons:</strong> We may disclose your information when necessary to
        comply with applicable laws, regulations, legal processes, or governmental requests.
      </li>
    </ul>

    <h2>8. International Data Transfers</h2>
    <p>
      Akuntsu and Butterfly have entered into a Data Processing Agreement (DPA) and Transfer Impact
      Assessment (TIA) to ensure that any transfers of personal data from Canada to Serbia are
      conducted in compliance with Law 25 and provide an adequate level of protection. The DPA
      incorporates the EU Standard Contractual Clauses (Module Two: Controller to Processor).
    </p>
    <p>
      Additionally, transfers of personal data to sub-processors located in the United States
      (Airtable Inc. and Automattic Inc.) are governed by executed Standard Contractual Clauses
      (SCCs) ensuring an equivalent level of data protection. You can request copies of relevant
      DPAs by contacting our Privacy Officer at{' '}
      <ExternalLink href="mailto:privacy@attractr.live">privacy@attractr.live</ExternalLink>.
    </p>

    <h2>9. Your Rights</h2>
    <p>
      Depending on your location, you may have rights under Law 25, PIPEDA, or the GDPR, including:
    </p>
    <ul>
      <li>The right to access and obtain a copy of your personal information.</li>
      <li>The right to request rectification of inaccurate personal information.</li>
      <li>The right to request deletion of your personal information.</li>
      <li>The right to data portability.</li>
      <li>
        The right to object to the processing of your personal information for certain purposes.
      </li>
      <li>The right to withdraw your consent to the processing of your personal information.</li>
      <li>The right to lodge a complaint with a supervisory authority.</li>
    </ul>
    <p>
      To exercise any of these rights, please contact our Privacy Officer at{' '}
      <ExternalLink href="mailto:privacy@attractr.live">privacy@attractr.live</ExternalLink>. We
      will respond within 30 days, as required by Law 25.
    </p>

    <h2>10. Cookies and Tracking Technologies</h2>
    <p>
      We use cookies and similar tracking technologies to collect information about your use of the
      Services. We use essential cookies (necessary for functionality) and analytics cookies (to
      measure and improve performance).
    </p>
    <p>
      You can control cookies through your browser settings. For users in the European Economic
      Area, we obtain consent before setting non-essential cookies.
    </p>

    <h2>11. Data Retention and Deletion</h2>
    <ul>
      <li>Account data: Retained until account deletion.</li>
      <li>Financial data: Retained for 7 years for tax compliance.</li>
      <li>Security logs: Retained for 12 months for incident investigation and prevention.</li>
      <li>
        CRM records (Airtable): Member and contact data retained for the duration of active
        membership plus 24 months, unless earlier deletion is requested.
      </li>
    </ul>
    <p>
      Content you have shared publicly on the Services, such as forum posts or comments, may remain
      visible after your account is deleted — see Section 7 of the Terms of Service for details on
      anonymization.
    </p>

    <h2>12. Security and Incident Response</h2>
    <p>
      We implement reasonable technical and organizational measures to protect your personal
      information from unauthorized access, disclosure, alteration, and destruction. These measures
      include:
    </p>
    <ul>
      <li>Mandatory Multi-Factor Authentication (MFA) across all administrative access layers.</li>
      <li>Encryption of data at rest and in transit.</li>
      <li>Continuous cryptographic logging of system initializations and access events.</li>
    </ul>
    <p>
      If we become aware of a security breach that compromises your personal information and creates
      a high risk to your rights and freedoms, we will notify the Commission d'accès à l'information
      (CAI) of Quebec and all affected data subjects within 72 hours of breach detection, as
      required by Law 25. We maintain an internal register of all confidentiality incidents.
    </p>

    <h2>13. Future Artificial Intelligence and Machine Learning Training</h2>
    <p>
      Our proprietary artificial intelligence engine, ClaraLive.ai, is currently under active
      technical development and is not operational, deployed, or active within the current launch
      phase of the Audience Lab platform.
    </p>
    <p>
      By default, your user content and personal data are not used to train our AI models. Any
      utilization of your data for machine learning training strictly requires your separate,
      explicit, and affirmative opt-in consent. This option is completely disabled and unchecked by
      default upon registration.
    </p>
    <p>
      Before any future AI model training takes place, all data is subjected to irreversible
      cryptographic anonymization protocols, removing all Personally Identifiable Information (PII).
      You maintain the absolute right to withdraw this processing authorization at any time via a
      self-service toggle within your account privacy settings, without penalty to your core
      platform access.
    </p>

    <h2>14. Changes to This Privacy Policy</h2>
    <p>
      We may update this Privacy Policy from time to time. We will post any changes on this page and
      indicate the date of the last revision. If we make material changes that affect your rights,
      we will provide additional notice and seek your consent as required by law.
    </p>

    <h2>15. Language</h2>
    <p>
      This Privacy Policy is available in both English and French. For users located in Quebec, the
      French version shall prevail where required by applicable law.
    </p>

    <h2>16. Contact Us</h2>
    <p>
      If you have any questions about this Privacy Policy, please contact:{' '}
      <ExternalLink href="mailto:privacy@attractr.live">privacy@attractr.live</ExternalLink>
    </p>

    {/* Sub-Processor Transparency Registry — required under Law 25
        and GDPR Article 28. Placed here so it lives with the rest of
        the privacy content. */}
    <h2>Appendix: Sub-Processor Transparency Registry</h2>
    <p className={css.appendixSubtitle}>
      Updated: June 2026 · Pursuant to Quebec Law 25 and GDPR Article 28
    </p>
    <p>
      In accordance with statutory data protection regulations, Akuntsu Inc. maintains this public
      registry of all authorized third-party sub-processors. These entities process personal data of
      registered Audience Lab platform users strictly under executed data protection instruments.
    </p>
    <p>
      The following sub-processors have been added or updated since the previous version of this
      registry: Airtable Inc. and Automattic Inc. (WordPress). All existing sub-processor agreements
      have been reviewed for continued compliance.
    </p>
    <table className={css.subProcessorTable}>
      <thead>
        <tr>
          <th>Entity</th>
          <th>Location</th>
          <th>Processing Activity</th>
          <th>Data Protection Instrument</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Brevo SAS</td>
          <td>Paris, France</td>
          <td>Newsletter ingestion, system notifications, automated outreach emails</td>
          <td>Signed DPA / European Data Hosting Compliance</td>
        </tr>
        <tr>
          <td>Airtable Inc.</td>
          <td>San Francisco, USA</td>
          <td>CRM / member database management</td>
          <td>Signed DPA / Standard Contractual Clauses (SCCs)</td>
        </tr>
        <tr>
          <td>Automattic Inc. (WordPress)</td>
          <td>San Francisco, USA</td>
          <td>Website hosting, blog publishing, plugin interactions</td>
          <td>Signed DPA / Standard Contractual Clauses (SCCs)</td>
        </tr>
      </tbody>
    </table>
    <p>
      Pursuant to valid transparency clauses, Akuntsu Inc. will notify all platform participants at
      least 14 days prior to onboarding any new or replacement sub-processor. Users maintain a
      statutory right to object to new data infrastructure routings by contacting{' '}
      <ExternalLink href="mailto:privacy@attractr.live">privacy@attractr.live</ExternalLink>.
    </p>
  </>
);

const FrenchPendingBanner = () => (
  <div className={css.translationNotice}>
    <p>
      <strong>Mise à jour en cours.</strong> La version française de cette Politique de
      confidentialité est en cours de traduction. La version anglaise ci-dessous est la version en
      vigueur jusqu'à publication de la traduction française officielle.
    </p>
  </div>
);

const PrivacyPolicy = props => {
  const { rootClassName, className } = props;
  const classes = classNames(rootClassName || css.root, className);
  const isFrench = useIntl().locale === 'fr';

  return (
    <div className={classes}>
      {isFrench ? <FrenchPendingBanner /> : null}
      <EnglishContent />
    </div>
  );
};

PrivacyPolicy.defaultProps = {
  rootClassName: null,
  className: null,
};

PrivacyPolicy.propTypes = {
  rootClassName: PropTypes.string,
  className: PropTypes.string,
};

export default PrivacyPolicy;
